// Quick guide — Word Classes (parts of speech). The building blocks of any
// language: what each kind of word DOES (its function), with an Italian example.
// Most beginners have forgotten these since school, yet every grammar rule is
// phrased in their terms — so this is the foundation guide. Each card links to
// the fuller reference view for that class. Hand-drawn paper style with audio.
import { navigate } from "../router/router.js";
import { speakButtonHTML } from "../lib/audio.js";

// Each class: the colour, emoji, name, the job it does, an Italian example with
// the class-word in <b>, its English gloss, and the existing view to open.
const CLASSES = [
  {
    color: "text-emerald-700",
    emoji: "📦",
    name: "Noun",
    es: "nome",
    job: "Names a person, place, thing, or idea. In Italian every noun also has a gender (il / la).",
    example: "<b>Il cane</b> dorme.",
    gloss: "The dog sleeps.",
    nav: "nouns",
  },
  {
    color: "text-rose-600",
    emoji: "🏃",
    name: "Verb",
    es: "verbo",
    job: "Expresses an action or a state of being. It changes form (conjugates) to show who and when.",
    example: "Lei <b>canta</b> bene.",
    gloss: "She sings well.",
    nav: "search",
  },
  {
    color: "text-amber-700",
    emoji: "🎨",
    name: "Adjective",
    es: "aggettivo",
    job: "Describes a noun. In Italian it agrees in gender and number, and usually comes after the noun.",
    example: "Una casa <b>bianca</b>.",
    gloss: "A white house.",
    nav: "adjectives",
  },
  {
    color: "text-sky-700",
    emoji: "🔖",
    name: "Article",
    es: "articolo",
    job: "A small word before a noun marking it as definite (the) or indefinite (a). Matches gender & number.",
    example: "<b>Il</b> libro e <b>un</b> tavolo.",
    gloss: "The book and a table.",
    nav: "articles",
  },
  {
    color: "text-violet-700",
    emoji: "👤",
    name: "Pronoun",
    es: "pronome",
    job: "Stands in for a noun so you don't repeat it — I, you, it, him, this, who…",
    example: "<b>Io</b> <b>la</b> vedo.",
    gloss: "I see her.",
    nav: "pronouns",
  },
  {
    color: "text-teal-700",
    emoji: "⚡",
    name: "Adverb",
    es: "avverbio",
    job: "Describes a verb, an adjective, or another adverb — how, when, where, or how much.",
    example: "Parla <b>molto piano</b>.",
    gloss: "He speaks very slowly.",
    nav: "adverbs",
  },
  {
    color: "text-indigo-700",
    emoji: "🔗",
    name: "Preposition",
    es: "preposizione",
    job: "Links a noun to the rest of the sentence — showing place, time, or direction (in, on, to, with).",
    example: "Il gatto è <b>nella</b> scatola.",
    gloss: "The cat is in the box.",
    nav: "prepositions",
  },
  {
    color: "text-orange-700",
    emoji: "➕",
    name: "Conjunction",
    es: "congiunzione",
    job: "Joins words, phrases, or clauses together — and, or, but, because.",
    example: "Caffè <b>e</b> pane, <b>ma</b> senza zucchero.",
    gloss: "Coffee and bread, but without sugar.",
    nav: "conjunctions",
  },
  {
    color: "text-pink-600",
    emoji: "💬",
    name: "Interjection",
    es: "interiezione",
    job: "A short outburst of emotion or reaction — it stands on its own.",
    example: "<b>Ahi!</b> <b>Ciao!</b>",
    gloss: "Ouch! Hello!",
    nav: "interjections",
  },
];

function classCard(c) {
  return `
    <div class="sketch-box-sm bg-white/30 p-4 mb-4 break-inside-avoid">
      <div class="flex items-center gap-2">
        <span class="text-2xl leading-none" aria-hidden="true">${c.emoji}</span>
        <h3 class="${c.color} text-2xl sketch-underline inline-block">${c.name}</h3>
        <span class="text-base text-sage-400" style="font-family:Inter,sans-serif">${c.es}</span>
      </div>
      <p class="text-sage-700 mt-2">${c.job}</p>
      <p class="text-sage-800 mt-2">${c.example}${speakButtonHTML(c.example.replace(/<[^>]+>/g, ""))} <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${c.gloss}</span></p>
      <button data-go="${c.nav}" class="mt-2 text-sm text-sage-600 underline decoration-sage-300 hover:text-sage-800" style="font-family:Inter,sans-serif">Open the ${c.name} reference →</button>
    </div>`;
}

export function wordClassesView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Word Classes 🧱</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Every word does a job in a sentence, and its job is its <i>class</i> (or “part of speech”). Grammar rules are all written in these terms — “the adjective agrees with the noun”, “put the pronoun before the verb” — so knowing the building blocks makes every later rule click into place.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        <p class="text-sage-700 mb-3">The nine you'll meet again and again, each with the one job it does:</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${CLASSES.map(classCard).join("")}
        </div>
        <p class="text-sage-700 mt-2 text-base" style="font-family:Inter,sans-serif">💡 The same word can change class with use: <b>il potere</b> (the power — noun) vs <b>potere</b> parlare (to be able — verb). Its job in <i>that</i> sentence decides its class.</p>
      </div>

      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Tap any “Open the … reference” to dive into how that class works in Italian — agreement, placement, and the common words.</p>
    </section>`;

  app.querySelectorAll("[data-go]").forEach((b) =>
    b.addEventListener("click", () => navigate(b.dataset.go))
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
